import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { CalendarIcon, MapPin, GraduationCap, X, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { createTutorProfile, getMyTutorProfile, updateTutorProfile } from '../../services/authApi';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '400px', borderRadius: '8px' };
const defaultCenter = { lat: 28.6139, lng: 77.2090 };
const libraries = ['places'];

export const EnhancedTutorProfile = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(editMode);
  const [date, setDate] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectInput, setSubjectInput] = useState('');
  const [teachingModes, setTeachingModes] = useState([]);
  const [spokenLanguages, setSpokenLanguages] = useState([]);
  const [languageInput, setLanguageInput] = useState('');
  const [teachingCategories, setTeachingCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [educationHistory, setEducationHistory] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [achievementInput, setAchievementInput] = useState('');
  const [awards, setAwards] = useState([]);
  const [awardInput, setAwardInput] = useState('');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    dob: '',
    gender: '',
    qualification: '',
    experience_years: 0,
    hourly_rate: 400,
    monthly_rate: null,
    salary_type: 'hourly',
    bio: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    intro_video_url: '',
    latitude: null,
    longitude: null,
    photo_url: ''
  });

  // Fetch existing profile data in edit mode
  useEffect(() => {
    if (editMode && token) {
      fetchExistingProfile();
    }
  }, [editMode, token]);

  const fetchExistingProfile = async () => {
    try {
      setFetchingProfile(true);
      const profile = await getMyTutorProfile();

      // Populate form data
      setFormData({
        full_name: profile.full_name || '',
        username: profile.username || '',
        dob: profile.dob || '',
        gender: profile.gender || '',
        qualification: profile.qualification || '',
        experience_years: profile.experience_years || 0,
        hourly_rate: profile.hourly_rate || 400,
        monthly_rate: profile.monthly_rate || null,
        salary_type: profile.salary_type || 'hourly',
        bio: profile.bio || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        pincode: profile.pincode || '',
        intro_video_url: profile.intro_video_url || '',
        latitude: profile.latitude || null,
        longitude: profile.longitude || null,
        photo_url: profile.photo_url || ''
      });

      // Populate array fields
      if (profile.subjects) setSubjects(profile.subjects);
      if (profile.teaching_mode) setTeachingModes(profile.teaching_mode);
      if (profile.spoken_languages) setSpokenLanguages(profile.spoken_languages);
      if (profile.teaching_category) setTeachingCategories(profile.teaching_category);
      if (profile.education_history) setEducationHistory(profile.education_history);
      if (profile.achievements) setAchievements(profile.achievements);
      if (profile.awards_certifications) setAwards(profile.awards_certifications);

      // Set date if exists
      if (profile.dob) {
        setDate(new Date(profile.dob));
      }

      // Set photo preview
      if (profile.photo_url) {
        setPhotoPreview(profile.photo_url);
      }

      // Set map position
      if (profile.latitude && profile.longitude) {
        const position = { lat: profile.latitude, lng: profile.longitude };
        setMapCenter(position);
        setMarkerPosition(position);
      }

      toast.success('Profile loaded successfully');
    } catch (error) {
      console.log('No existing profile found or error:', error);
      if (editMode) {
        toast.info('No existing profile found. Create a new one.');
      }
    } finally {
      setFetchingProfile(false);
    }
  };

  const teachingModeOptions = [
    { value: 'online', label: 'Online' },
    { value: 'offline_tutor_place', label: "At Tutor's Place" },
    { value: 'offline_student_place', label: "At Student's Place" },
    { value: 'nearby_classes', label: 'Nearby Classes' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setFormData({ ...formData, dob: format(selectedDate, 'yyyy-MM-dd') });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.error('Image size should be less than 500KB. Please compress your image before uploading.');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData({ ...formData, photo_url: '' });
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      const data = await apiClient.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return data.url;
    } catch (error) {
      return photoPreview;
    }
  };

  const addSubject = () => {
    if (subjectInput.trim() && !subjects.includes(subjectInput.trim())) {
      setSubjects([...subjects, subjectInput.trim()]);
      setSubjectInput('');
    }
  };

  const removeSubject = (subject) => {
    setSubjects(subjects.filter(s => s !== subject));
  };

  const toggleTeachingMode = (mode) => {
    setTeachingModes(prev =>
      prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]
    );
  };

  // Spoken Languages handlers
  const addLanguage = () => {
    if (languageInput.trim() && !spokenLanguages.includes(languageInput.trim())) {
      setSpokenLanguages([...spokenLanguages, languageInput.trim()]);
      setLanguageInput('');
    }
  };
  const removeLanguage = (lang) => setSpokenLanguages(spokenLanguages.filter(l => l !== lang));

  // Teaching Categories handlers
  const addCategory = () => {
    if (categoryInput.trim() && !teachingCategories.includes(categoryInput.trim())) {
      setTeachingCategories([...teachingCategories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };
  const removeCategory = (cat) => setTeachingCategories(teachingCategories.filter(c => c !== cat));

  // Achievements handlers
  const addAchievement = () => {
    if (achievementInput.trim() && !achievements.includes(achievementInput.trim())) {
      setAchievements([...achievements, achievementInput.trim()]);
      setAchievementInput('');
    }
  };
  const removeAchievement = (ach) => setAchievements(achievements.filter(a => a !== ach));

  // Awards handlers
  const addAward = () => {
    if (awardInput.trim() && !awards.includes(awardInput.trim())) {
      setAwards([...awards, awardInput.trim()]);
      setAwardInput('');
    }
  };
  const removeAward = (aw) => setAwards(awards.filter(a => a !== aw));

  // Education History handlers
  const [newEducation, setNewEducation] = useState({ degree: '', institution: '', year: '' });
  const addEducation = () => {
    if (newEducation.degree.trim() && newEducation.institution.trim()) {
      setEducationHistory([...educationHistory, { ...newEducation }]);
      setNewEducation({ degree: '', institution: '', year: '' });
    }
  };
  const removeEducation = (index) => setEducationHistory(educationHistory.filter((_, i) => i !== index));

  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const components = results[0].address_components;
        let city = '', state = '', pincode = '';
        components.forEach(c => {
          if (c.types.includes('locality')) city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
          if (c.types.includes('postal_code')) pincode = c.long_name;
        });
        setFormData(prev => ({
          ...prev,
          address: results[0].formatted_address,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode
        }));
      }
    });
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });

        const components = place.address_components || [];
        let city = '', state = '', pincode = '';
        components.forEach(c => {
          if (c.types.includes('locality')) city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
          if (c.types.includes('postal_code')) pincode = c.long_name;
        });

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          city, state, pincode,
          latitude: lat,
          longitude: lng
        }));
      }
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
          toast.success('Location captured!');
        },
        () => toast.error('Unable to fetch location')
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (subjects.length === 0) {
      toast.error('Please add at least one subject');
      return;
    }
    if (teachingModes.length === 0) {
      toast.error('Please select at least one teaching mode');
      return;
    }

    setLoading(true);
    try {
      let photoUrl = formData.photo_url;
      if (photoFile) photoUrl = await uploadPhoto();

      const profileData = {
        ...formData,
        photo_url: photoUrl,
        subjects,
        teaching_mode: teachingModes,
        experience_years: parseInt(formData.experience_years),
        hourly_rate: parseInt(formData.hourly_rate),
        monthly_rate: formData.monthly_rate ? parseInt(formData.monthly_rate) : null,
        spoken_languages: spokenLanguages,
        education_history: educationHistory,
        achievements,
        awards_certifications: awards,
        teaching_category: teachingCategories
      };

      if (editMode) {
        await updateTutorProfile(profileData);
        toast.success('Profile updated successfully!');
      } else {
        await createTutorProfile(profileData);
        toast.success('Profile created! Under review.');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} profile`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'tutor') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-purple-600 p-3 rounded-lg">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">{editMode ? 'Edit' : 'Create'} Tutor Profile</CardTitle>
                <CardDescription>{editMode ? 'Update your profile information' : 'Share your expertise and start teaching'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Photo Upload */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Photo</h3>
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {photoPreview ? (
                      <div className="relative">
                        <img src={photoPreview} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-purple-100" />
                        <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                        <GraduationCap className="h-16 w-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="photo" className="cursor-pointer">
                      <div className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                        <Upload className="h-4 w-4" />
                        <span>Upload Photo</span>
                      </div>
                      <Input id="photo" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                    </Label>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF (max 500KB)</p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Full Name *</Label><Input name="full_name" value={formData.full_name} onChange={handleChange} required /></div>
                  <div><Label>Username *</Label><Input name="username" value={formData.username} onChange={handleChange} required /></div>
                  <div>
                    <Label>Date of Birth *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={date} onSelect={handleDateSelect} disabled={(date) => date > new Date()} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Professional Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Qualification *</Label><Input name="qualification" placeholder="e.g., B.Tech, M.Sc" value={formData.qualification} onChange={handleChange} required /></div>
                  <div><Label>Experience (years) *</Label><Input name="experience_years" type="number" min="0" value={formData.experience_years} onChange={handleChange} required /></div>
                  <div>
                    <Label>Salary Type</Label>
                    <select
                      name="salary_type"
                      value={formData.salary_type}
                      onChange={handleChange}
                      className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="monthly">Monthly</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                  <div><Label>Hourly Rate (₹)</Label><Input name="hourly_rate" type="number" min="100" value={formData.hourly_rate} onChange={handleChange} /></div>
                  <div><Label>Monthly Rate (₹)</Label><Input name="monthly_rate" type="number" min="1000" value={formData.monthly_rate || ''} onChange={handleChange} placeholder="Optional" /></div>
                  <div className="md:col-span-2"><Label>Bio *</Label><Textarea name="bio" value={formData.bio} onChange={handleChange} placeholder="Tell students about yourself, your teaching style, and what makes you unique..." rows={4} required /></div>
                  <div className="md:col-span-2">
                    <Label>Intro Video URL</Label>
                    <Input
                      name="intro_video_url"
                      value={formData.intro_video_url}
                      onChange={handleChange}
                      placeholder="YouTube or Google Drive link to your introduction video"
                    />
                    <p className="text-xs text-gray-500 mt-1">Share a video introducing yourself to potential students</p>
                  </div>
                </div>
              </div>

              {/* Education History */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Education History</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-4 gap-2">
                    <Input
                      value={newEducation.degree}
                      onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })}
                      placeholder="Degree (e.g., B.Tech)"
                    />
                    <Input
                      value={newEducation.institution}
                      onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })}
                      placeholder="Institution"
                    />
                    <Input
                      value={newEducation.year}
                      onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })}
                      placeholder="Year"
                    />
                    <Button type="button" onClick={addEducation}>Add</Button>
                  </div>
                  {educationHistory.length > 0 && (
                    <div className="space-y-2">
                      {educationHistory.map((edu, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="font-medium">{edu.degree}</span>
                            <span className="text-gray-500"> from </span>
                            <span>{edu.institution}</span>
                            {edu.year && <span className="text-gray-500"> ({edu.year})</span>}
                          </div>
                          <button type="button" onClick={() => removeEducation(i)} className="text-red-500 hover:text-red-700">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Spoken Languages */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Spoken Languages</h3>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      placeholder="e.g., English, Hindi, Tamil"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                    />
                    <Button type="button" onClick={addLanguage}>Add</Button>
                  </div>
                  {spokenLanguages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {spokenLanguages.map((lang, i) => (
                        <Badge key={i} variant="secondary" className="px-3 py-1">
                          {lang}
                          <button type="button" onClick={() => removeLanguage(lang)} className="ml-2 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Teaching Categories */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Teaching Categories</h3>
                <p className="text-sm text-gray-500 mb-3">E.g., Primary Education, High School, Competitive Exams, College Level</p>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      placeholder="Enter teaching category"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    />
                    <Button type="button" onClick={addCategory}>Add</Button>
                  </div>
                  {teachingCategories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {teachingCategories.map((cat, i) => (
                        <Badge key={i} variant="outline" className="px-3 py-1 border-purple-300 bg-purple-50">
                          {cat}
                          <button type="button" onClick={() => removeCategory(cat)} className="ml-2 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Achievements & Awards */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Achievements</h3>
                  <div className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        value={achievementInput}
                        onChange={(e) => setAchievementInput(e.target.value)}
                        placeholder="Add achievement"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
                      />
                      <Button type="button" onClick={addAchievement} size="sm">Add</Button>
                    </div>
                    {achievements.length > 0 && (
                      <div className="space-y-2">
                        {achievements.map((ach, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <span className="text-sm">{ach}</span>
                            <button type="button" onClick={() => removeAchievement(ach)} className="text-red-500 hover:text-red-700">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">Awards & Certifications</h3>
                  <div className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        value={awardInput}
                        onChange={(e) => setAwardInput(e.target.value)}
                        placeholder="Add award or certification"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAward())}
                      />
                      <Button type="button" onClick={addAward} size="sm">Add</Button>
                    </div>
                    {awards.length > 0 && (
                      <div className="space-y-2">
                        {awards.map((aw, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-200">
                            <span className="text-sm">{aw}</span>
                            <button type="button" onClick={() => removeAward(aw)} className="text-red-500 hover:text-red-700">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subjects */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Subjects You Teach</h3>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input value={subjectInput} onChange={(e) => setSubjectInput(e.target.value)} placeholder="Enter subject" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())} />
                    <Button type="button" onClick={addSubject}>Add</Button>
                  </div>
                  {subjects.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {subjects.map((s, i) => (
                        <Badge key={i} variant="secondary" className="px-3 py-1">
                          {s}
                          <button type="button" onClick={() => removeSubject(s)} className="ml-2 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Teaching Modes */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Teaching Modes</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {teachingModeOptions.map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => toggleTeachingMode(mode.value)}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${teachingModes.includes(mode.value)
                        ? 'border-purple-600 bg-purple-50 text-purple-900'
                        : 'border-gray-200 hover:border-purple-300'
                        }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location with Google Maps */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Location Information</h3>
                <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                  <div className="space-y-4">
                    <div><Label>Search Address</Label><Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}><Input placeholder="Type to search..." /></Autocomplete></div>
                    <Button type="button" variant="outline" onClick={getCurrentLocation}><MapPin className="h-4 w-4 mr-2" />Use Current Location</Button>
                    <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13} onClick={onMapClick}>
                      {markerPosition && <Marker position={markerPosition} />}
                    </GoogleMap>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2"><Label>Address *</Label><Input name="address" value={formData.address} onChange={handleChange} required /></div>
                      <div><Label>City *</Label><Input name="city" value={formData.city} onChange={handleChange} required /></div>
                      <div><Label>State *</Label><Input name="state" value={formData.state} onChange={handleChange} required /></div>
                      <div><Label>Pincode</Label><Input name="pincode" value={formData.pincode} onChange={handleChange} /></div>
                    </div>
                    {formData.latitude && formData.longitude && (
                      <p className="text-sm text-green-600">Location: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}</p>
                    )}
                  </div>
                </LoadScript>
              </div>

              {/* Submit */}
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>Cancel</Button>
                <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                  {loading ? 'Creating...' : 'Create Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};